import { Component } from "react"
import PropTypes from "prop-types"

import { createOrderByFilterGroup, genericFilterGroup } from "shared/search/containers/FilterGroup/FilterGroups"

import {
    federalRegions,
    stateRegions,
    localRegions,
    federalAndStates,
    usRegions,
    usEURegion,
    euRegions,
} from "shared/imports/regionConstants"

import {
    DateListFilter,
    CommitteeMentionFilter,
    BillMentionFilter,
    VoteMentionFilter,
    RegionFilter,
    RegionCountryEUFilter,
} from "shared/search/containers/Filter/Filters"
import { inIssueFilter, notInIssueFilter } from "shared/search/containers/Filter/FilterCreators"

import {
    EventDatePickerStartFilter,
    EventDatePickerOnFilter,
    EventDatePickerEndFilter,
    EventTypeFilter,
    EventTypeEUFilter,
    EventChamberFilter,
    EventWitnessMentionFilter,
    EventPointPersonFilter,
    EventSchoolDistrictFilter,
    EventSponsoringOrganizationFilter,
    EventParticipatingOfficialsFilter,
    EventParticipatingUsersFilter,
    EventParticipatingOrganizationsFilter,
    EventParticipatingSupportersFilter,
    EventAmendmentMentionFilter,
    EventIssueMentionFilter,
    EventCommitteeTranscriptsFilter,
    EventCommitteeVerifiedTranscriptsFilter,
    EventInvisibleCommitteeFilter,
    // Organization profile
    EventOrganizatonsFilter,
    // Committee profile
    EventCommitteProfileFilter,
    EventCountyFilter,
    EventMunicipalityFilter,
    // Copilot
    EventSupportedRegionsFilter,
    EventRepresentingSchoolDistrictEnrollmentMaxFilter,
    EventRepresentingSchoolDistrictEnrollmentMinFilter,
} from "shared/search/containers/Filter/EventFilters"

// helpers
import isFeatureEnabled from "shared/featureflags/helperFunctions"
import { checkIfShouldShowSchoolBoard } from "shared/imports/permissionFunctions"

const { isExclusivelySchoolBoard, canSeeSchoolBoards } = checkIfShouldShowSchoolBoard()

export class EventDateFilterGroup extends Component {
    static defaultProps = {
        filters: [DateListFilter, EventDatePickerOnFilter, EventDatePickerStartFilter, EventDatePickerEndFilter],
        uniqueFilterGroupId: "eventDateFilterGroup",
        displayLabel: "Date",
        helpText: "Filter events by specific date occurred or by time frame.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class EventTypeFilterGroup extends Component {
    static defaultProps = {
        filters: [
            EventTypeFilter,
            // For School Boards, we need to check if the user doesn't have other local permissions.
            // This is due the overlapping between both permissions and what we actually need to show in the filter.
            ...(isExclusivelySchoolBoard
                ? []
                : [EventTypeEUFilter, EventCommitteeTranscriptsFilter, EventCommitteeVerifiedTranscriptsFilter]),
        ],
        uniqueFilterGroupId: "eventTypeFilterGroup",
        displayLabel: "Event Type",
        helpText: "Filter events by legislative session, deadlines, or other type.",
        permissions: [DjangIO.app.models.PermissionType.legislative_profiles],
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class EventChamberFilterGroup extends Component {
    static defaultProps = {
        filters: [EventChamberFilter],
        uniqueFilterGroupId: "eventChamberFilterGroup",
        displayLabel: "Chamber",
        permissions: [DjangIO.app.models.PermissionType.legislative_profiles],
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class EventMentionFilterGroup extends Component {
    static defaultProps = {
        filters: [
            CommitteeMentionFilter,
            EventInvisibleCommitteeFilter,
            BillMentionFilter,
            VoteMentionFilter,
            EventAmendmentMentionFilter,
            EventWitnessMentionFilter,
            EventIssueMentionFilter,
        ],
        uniqueFilterGroupId: "eventMentionFilterGroupFederal",
        displayLabel: "Referencing",
        helpText: "Filter events by related committee, bill, vote, legislator, etc.",
        regions: usEURegion,
        permissions: [DjangIO.app.models.PermissionType.legislative_profiles],
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class EventParticipantFilterGroup extends Component {
    static defaultProps = {
        filters: [
            EventPointPersonFilter,
            EventSponsoringOrganizationFilter,
            EventParticipatingOfficialsFilter,
            EventParticipatingUsersFilter,
            EventParticipatingOrganizationsFilter,
            EventParticipatingSupportersFilter,
        ],
        uniqueFilterGroupId: "eventParticipantFilterGroup",
        displayLabel: "Custom Events Participants",
        helpText:
            "Filter for events based on their point person, sponsoring organization, " + "or the event's participants.",
        permissions: [DjangIO.app.models.PermissionType.custom_events],
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class EventProjectFilterGroup extends Component {
    static defaultProps = {
        filters: [inIssueFilter, notInIssueFilter],
        uniqueFilterGroupId: "eventProjectFilterGroup",
        displayLabel: "Issues",
        helpText: "Filter for events by tagged Issues.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

const defaultProps = {
    filters: PropTypes.array,
    uniqueFilterGroupId: PropTypes.string,
    displayLabel: PropTypes.string,
    helpText: PropTypes.string,
}

export const EventOrganizationProfileFilterGroup = (props) => genericFilterGroup(props)
EventOrganizationProfileFilterGroup.propTypes = defaultProps
EventOrganizationProfileFilterGroup.defaultProps = {
    filters: [EventOrganizatonsFilter],
    uniqueFilterGroupId: "eventOrganizationProfileFilterGroup",
    noHeader: true,
    alwaysOpen: true,
}

export const EventCommitteeProfileFilterGroup = (props) => genericFilterGroup(props)
EventCommitteeProfileFilterGroup.propTypes = defaultProps
EventCommitteeProfileFilterGroup.defaultProps = {
    filters: [EventCommitteProfileFilter],
    uniqueFilterGroupId: "eventCommitteeProfileFilterGroup",
    noHeader: true,
    alwaysOpen: true,
}

export const EventRegionFilterGroup = (props) => genericFilterGroup(props)
EventRegionFilterGroup.propTypes = defaultProps
EventRegionFilterGroup.defaultProps = {
    filters: [
        RegionFilter,
        EventCountyFilter,
        EventMunicipalityFilter,
        ...(canSeeSchoolBoards
            ? [
                  EventSchoolDistrictFilter,
                  ...(isFeatureEnabled("ff_school_board_orgs_and_officials")
                      ? [
                            EventRepresentingSchoolDistrictEnrollmentMinFilter,
                            EventRepresentingSchoolDistrictEnrollmentMaxFilter,
                        ]
                      : []),
              ]
            : []),
    ],
    uniqueFilterGroupId: "eventRegionFilterGroup",
    displayLabel: "Region",
    helpText: "Filter for results from a single region or a group of regions.",
}

export const EventOrderByFilterGroup = createOrderByFilterGroup({
    // uniqueFilterId = "eventOrderBy"
    quorumDataType: DjangIO.app.models.QuorumDataType.event.value,
})

export class EventSupportedRegionFilterGroup extends Component {
    static defaultProps = {
        filters: [EventSupportedRegionsFilter, RegionCountryEUFilter],
        uniqueFilterGroupId: "eventRegionFilterGroup",
        displayLabel: "Region",
        helpText: "Filter for event from a single region or group of regions.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}
